/**
 * @ngdoc    function
 * @module   csm.styles
 * @name     csm.styles.service:StyleRule
 * @param    {String|Array<String>} selector CSS selector string or Array of
 *                                           string selectors to use with the rules
 * @param    {Object}               styles   Object consisting of style values to
 *                                           associate with the rule. Property names
 *                                           should be actual css property names.
 * @constructor
 * @description
 * Constant constructor function to generate an object to create and manage a
 * style rule using the provided selector. This is done as a constant so it can
 * be used at the provider/config level if necessary.
 *******************************************************************************/
export function styleRule(selector, styles) {
  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Style DOM Element. */ var mElement = document.createElement('style');
  /** List of selectors. */ var mSelectors = [];
  /** Current style rules object. */ var mStyles = angular.extend({}, styles);

  /*****************************************************************************
   * Methods
   *****************************************************************************/
  /**
   * @ngdoc    method
   * @name     addSelector
   * @methodOf csm.styles.service:StyleRule
   * @param    {String|Array<String>} selector CSS selector string or Array of
   *                                           string selectors
   *
   * @description
   * Updates the selectors for the style rule if not associated.
   */
  this.addSelector = function (selector) {
    /*----------------*/
    /* Validate input */
    /*----------------*/
    if (!selector || !(angular.isString(selector) || angular.isArray(selector)))
      throw new Error('Invalid selector provided');

    /*-----------------*/
    /* Normalize input */
    /*-----------------*/
    var list = (
      angular.isString(selector) ? selector.split(',') : selector
    ).map(function (s) {
      return s.trim();
    });

    /*-------------------*/
    /* Add new selectors */
    /*-------------------*/
    list.forEach(function (s) {
      if (mSelectors.indexOf(s) == -1) mSelectors.push(s);
    });

    /*--------------------*/
    /* Update for changes */
    /*--------------------*/
    updateElement();
  };

  /**
   * @ngdoc    method
   * @name     setStyle
   * @methodOf csm.styles.service:StyleRule
   * @param    {String} style CSS style property name
   * @param    {String} value Corresponding string value
   *
   * @description
   * Adds/Updates/Removes a style property for the rule. If no value has been provided
   * the current property is removed.
   */
  this.setStyle = function (style, value) {
    /*----------------------------------*/
    /* Do nothing if no change in value */
    /*----------------------------------*/
    if (mStyles[style] == value) return;

    /*-------------------------------------------------*/
    /* Remove existing property when there is no value */
    /*-------------------------------------------------*/
    if (!value) delete mStyles[style];
    /*----------------------*/
    /* Otherwise add/update */
    /*----------------------*/ else mStyles[style] = value;

    /*--------------------*/
    /* Update for changes */
    /*--------------------*/
    updateElement();
  };

  /** Updates the style element for the selectors and style rules. */
  function updateElement() {
    var selectors = mSelectors.join(',');
    var styles = Object.keys(mStyles).reduce(function (rules, prop) {
      return rules + `${prop}:${mStyles[prop]} !important;`;
    }, '');

    mElement.innerHTML = `${selectors} { ${styles} }`;
  }

  /*----------------------------------------*/
  /* Add element to DOM in the head element */
  /*----------------------------------------*/
  document.querySelector('head').appendChild(mElement);

  /*-----------------------*/
  /* Load initial selector */
  /*-----------------------*/
  this.addSelector(selector);
}
